// SetupWizard.tsx — the SOLE customer-creation path.
//
// POST /tenants/provision-wizard is the only endpoint that runs collision
// classification before creating anything, and the only one that issues the
// admin's set-password invite. No other create form exists in this console on
// purpose (see the note at the top of Customers.tsx).
//
// COLLISION HANDLING IS THE POINT OF THE REVIEW STEP. The server answers 409 with
// one of three kinds, and they mean different things:
//   blocked          — an active customer already owns this name or admin email
//   ambiguous        — more than one existing record could match; a human must look
//   offboarded_match — a previously offboarded customer matches; offer reactivation
// Collapsing these into "creation failed" would push an operator toward creating a
// duplicate customer, which is the failure this classification exists to prevent.

import React, { useEffect, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import {
  PCard, PBtn, PBadge, PBanner, PInput, PSelect, PCheck, PSpinner, PSection, PId,
} from "./ui";

const STEPS = ["Identity", "Sign-in", "Team", "Integrations", "Defaults", "Review"] as const;
type Step = 0 | 1 | 2 | 3 | 4 | 5;

const INTEGRATIONS = ["harvey", "salesforce", "docusign", "sharepoint", "esign"] as const;

interface PlannedUser { email: string; role: string }

export const SetupWizard: React.FC<{ onProvisioned: (tenantId: string) => void }> = ({ onProvisioned }) => {
  const [step, setStep] = useState<Step>(0);

  // Identity
  const [name, setName] = useState("");
  const [edition, setEdition] = useState("firm");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");

  // Sign-in
  const [signin, setSignin] = useState<"sso" | "password">("password");
  const [ssoDomains, setSsoDomains] = useState("");

  // Team
  const [entities, setEntities] = useState("");
  const [planned, setPlanned] = useState<PlannedUser[]>([]);
  const [pu, setPu] = useState<PlannedUser>({ email: "", role: "requester" });

  // Integrations
  const [ints, setInts] = useState<Record<string, boolean>>({});
  const [ipAllowlist, setIpAllowlist] = useState("");

  // Defaults
  const [sections, setSections] = useState<any[] | null>(null);
  const [include, setInclude] = useState<Record<string, boolean>>({});

  // Submit
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [collision, setCollision] = useState<null | { kind: string; message: string; tenant_id?: string; tenant_name?: string; status?: string }>(null);
  const [done, setDone] = useState<null | { tenant_id: string; admin_email: string }>(null);

  useEffect(() => {
    (async () => {
      const r = await pget("/provisioning-defaults");
      if (!r.ok) { setSections([]); return; }
      const s = r.data?.sections || [];
      setSections(s);
      // Default to copying every surface; the operator opts OUT. A default of
      // "copy nothing" quietly provisions an empty tenant that looks broken.
      setInclude(Object.fromEntries(s.map((x: any) => [x.section, true])));
    })();
  }, []);

  // 2FA is forced whenever sign-in is password-based; the toggle is not offered
  // as a choice because an unforced password tenant is the weakest configuration
  // the wizard can produce.
  const force2fa = signin === "password";

  const canNext = (): boolean => {
    if (step === 0) return !!name.trim() && !!adminEmail.trim();
    if (step === 1) return signin === "password" || !!ssoDomains.trim();
    return true;
  };

  const submit = async (reason: string) => {
    setBusy(true); setErr(""); setCollision(null);
    const selections = Object.fromEntries(
      (sections || []).map((s: any) => [s.section, include[s.section] !== false]),
    );
    const onboarding = {
      signin_method: signin,
      force_2fa: force2fa,
      sso_domains: ssoDomains.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean),
      entities: entities.split(",").map((e) => e.trim()).filter(Boolean),
      planned_users: planned,
      integrations: Object.keys(ints).filter((k) => ints[k]),
      ip_allowlist: ipAllowlist.split(",").map((i) => i.trim()).filter(Boolean),
    };
    const r = await pmutate("POST", "/tenants/provision-wizard", reason, {
      name: name.trim(),
      edition,
      admin_email: adminEmail.trim().toLowerCase(),
      admin_name: adminName.trim() || "Tenant Admin",
      selections,
      fresh: {},
    });
    setBusy(false);

    if (r.status === 409) {
      const d = r.data || {};
      setCollision({ kind: d.error, message: d.message || "A conflicting record exists.", tenant_id: d.tenant_id, tenant_name: d.tenant_name, status: d.status });
      return;
    }
    if (!r.ok) { setErr(perr(r, "Provisioning failed")); return; }

    // SECOND CALL, RESTORED. This screen used to send `onboarding` inside the
    // provision body — and POST /tenants/provision-wizard never destructures
    // `onboarding` (platformAdmin.js:496), so every answer on this wizard was
    // read, posted, and dropped on the floor. Two visible consequences:
    //
    //   * the company entities typed here were never created, with no error,
    //     because the provision route creates entities from `fresh
    //     .company_entities` and this screen always sends `fresh: {}`;
    //   * the customer's Onboarding tab read "No onboarding profile recorded",
    //     truthfully — nothing had ever written one.
    //
    // The pre-redesign wizard did this in TWO steps and worked:
    // POST provision-wizard, then PUT /tenants/:id/onboarding
    // (SetupWizard.tsx:250 at commit 37b3283ca). That PUT is still there, still
    // records the profile AND provisions the entered entities and users. The
    // redesign simply stopped calling it. This restores the call rather than
    // teaching a second endpoint to do the same job.
    const tid = r.data?.tenant_id;
    if (tid) {
      const rec = await pmutate("PUT", `/tenants/${tid}/onboarding`, reason, onboarding);
      if (!rec.ok) {
        // Non-fatal and SAID SO — the customer exists either way. Silence here
        // is what let the original defect run unnoticed.
        setErr(
          `The customer was created, but recording the onboarding answers failed: ${perr(rec, "unknown error")}. ` +
          `The company entities entered here were not created. You can re-enter them on the customer's own screens.`,
        );
      }
    }
    setDone({ tenant_id: tid, admin_email: r.data?.admin_email || adminEmail });
  };

  const reactivate = async () => {
    if (!collision?.tenant_id) return;
    setBusy(true); setErr("");
    const r = await pmutate("POST", `/tenants/${collision.tenant_id}/reactivate-onboard`, `Reactivating offboarded customer matched during wizard provisioning of "${name.trim()}"`, {});
    setBusy(false);
    if (!r.ok) { setErr(perr(r, "Reactivation failed")); return; }
    setDone({ tenant_id: collision.tenant_id, admin_email: adminEmail });
  };

  if (done) {
    return (
      <PSection title="Customer provisioned">
        <PCard>
          <PBadge tone="ok">Provisioned</PBadge>
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <strong>{name || collision?.tenant_name}</strong> is ready.
          </div>
          <div style={{ marginTop: 7, fontSize: 12.5, color: PT.txM }}>
            A set-password invite was emailed to <strong>{done.admin_email}</strong>.
            No password was generated or displayed — the console never shows one.
          </div>
          <div style={{ marginTop: 7, fontSize: 12.5, color: PT.txM }}>
            Tenant id: <PId id={done.tenant_id} />
          </div>
          <div style={{ marginTop: 15 }}>
            <PBtn v="primary" onClick={() => onProvisioned(done.tenant_id)}>Open customer</PBtn>
          </div>
        </PCard>
      </PSection>
    );
  }

  return (
    <PSection title="Setup Wizard" sub="The only path that creates a customer">
      {/* Stepper */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {STEPS.map((s, i) => {
          const state = i === step ? "on" : i < step ? "done" : "todo";
          return (
            <div
              key={s}
              style={{
                fontSize: 11.5, fontWeight: state === "on" ? 600 : 500,
                padding: "5px 10px", borderRadius: PT.radius,
                color: state === "on" ? PT.acc : state === "done" ? PT.ok : PT.txD,
                background: state === "on" ? PT.accS : state === "done" ? PT.okS : PT.bg3,
                border: `1px solid ${state === "on" ? PT.accS2 : "transparent"}`,
              }}
            >
              {i + 1}. {s}
            </div>
          );
        })}
      </div>

      <PBanner>{err}</PBanner>

      {collision && (
        <PCard style={{ borderColor: PT.warn, marginBottom: 14 }}>
          <PBadge tone="warn">{collision.kind.replace(/_/g, " ")}</PBadge>
          <div style={{ marginTop: 9, fontSize: 12.5, color: PT.tx }}>{collision.message}</div>
          {collision.kind === "offboarded_match" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12.5, color: PT.txM, marginBottom: 9 }}>
                <strong>{collision.tenant_name}</strong> was offboarded ({collision.status}). Reactivating
                keeps its history; creating a new customer would duplicate it.
              </div>
              <PBtn v="primary" small disabled={busy} onClick={reactivate}>
                {busy ? "Reactivating…" : "Reactivate and onboard"}
              </PBtn>
            </div>
          )}
          {collision.kind === "ambiguous" && (
            <div style={{ marginTop: 9, fontSize: 12, color: PT.txM }}>
              More than one record could match. Resolve it in Customers before provisioning.
            </div>
          )}
        </PCard>
      )}

      <PCard>
        {step === 0 && (
          <>
            <PInput label="Organisation name" value={name} onChange={setName} autoFocus />
            <PSelect
              label="Edition" value={edition} onChange={setEdition}
              options={[{ value: "firm", label: "firm" }, { value: "in_house", label: "in_house" }]}
              hint="Determines the tenancy model and the default surface set."
            />
            <PInput label="Admin email" value={adminEmail} onChange={setAdminEmail} hint="Receives the set-password invite." />
            <PInput label="Admin name" value={adminName} onChange={setAdminName} placeholder="Tenant Admin" />
          </>
        )}

        {step === 1 && (
          <>
            <PSelect
              label="Sign-in method" value={signin} onChange={(v) => setSignin(v as "sso" | "password")}
              options={[{ value: "password", label: "Password + 2FA" }, { value: "sso", label: "Single sign-on" }]}
            />
            {signin === "sso" ? (
              <PInput
                label="Allowed email domains" value={ssoDomains} onChange={setSsoDomains}
                placeholder="acme.com, acme.co.uk"
                hint="Comma-separated. Only these domains may sign in via SSO."
              />
            ) : (
              <div style={{ fontSize: 12.5, color: PT.txM, background: PT.bgInset, border: `1px solid ${PT.brd}`, borderRadius: PT.radius, padding: 11 }}>
                Two-factor authentication is <strong>forced</strong> for password sign-in. This is not
                configurable here — a password tenant without 2FA is the weakest configuration the
                wizard can produce, so it is not offered.
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <PInput
              label="Company entities" value={entities} onChange={setEntities}
              placeholder="Acme Ltd, Acme GmbH"
              hint="Comma-separated legal entities."
            />
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD, marginBottom: 6 }}>
              Planned users
            </div>
            {planned.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12.5 }}>
                <span style={{ fontFamily: PT.fontMono }}>{p.email}</span>
                <PBadge tone="neutral">{p.role}</PBadge>
                <PBtn small v="ghost" onClick={() => setPlanned(planned.filter((_, j) => j !== i))}>Remove</PBtn>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 2 }}>
                <PInput label="Email" value={pu.email} onChange={(v) => setPu({ ...pu, email: v })} />
              </div>
              <div style={{ flex: 1 }}>
                <PSelect
                  label="Role" value={pu.role} onChange={(v) => setPu({ ...pu, role: v })}
                  options={[
                    { value: "requester", label: "requester" }, { value: "lawyer", label: "lawyer" },
                    { value: "legalops", label: "legalops" }, { value: "admin", label: "admin" },
                  ]}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <PBtn
                  small
                  disabled={!pu.email.trim()}
                  onClick={() => { setPlanned([...planned, { ...pu, email: pu.email.trim().toLowerCase() }]); setPu({ email: "", role: "requester" }); }}
                >Add</PBtn>
              </div>
            </div>
            <div style={{ fontSize: 11, color: PT.txD }}>
              Recorded on the onboarding profile. Users are invited from the customer's Users tab
              after provisioning — each receives a set-password link.
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD, marginBottom: 7 }}>
              Integrations to enable
            </div>
            {INTEGRATIONS.map((i) => (
              <PCheck key={i} checked={!!ints[i]} onChange={(v) => setInts({ ...ints, [i]: v })} label={i} />
            ))}
            <div style={{ marginTop: 12 }}>
              <PInput
                label="IP allowlist" value={ipAllowlist} onChange={setIpAllowlist}
                placeholder="203.0.113.0/24, 198.51.100.7"
                hint="Comma-separated. Leave empty for no IP restriction."
              />
            </div>
            <div style={{ fontSize: 11, color: PT.txD }}>
              Enabling an integration records the intent. <strong>Credentials are staged separately</strong>,
              per customer, on the Integrations tab — no secret is entered in this wizard.
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div style={{ fontSize: 12.5, color: PT.txM, marginBottom: 11 }}>
              Choose which provisioning-default surfaces are copied into the new customer.
              Copying is applied at provision time from the defaults store.
            </div>
            {sections === null ? <PSpinner /> : sections.length === 0 ? (
              <div style={{ fontSize: 12.5, color: PT.txD }}>No provisioning defaults are configured.</div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 8, marginBottom: 11 }}>
                  <PBtn small onClick={() => setInclude(Object.fromEntries(sections.map((s: any) => [s.section, true])))}>Select all</PBtn>
                  <PBtn small onClick={() => setInclude(Object.fromEntries(sections.map((s: any) => [s.section, false])))}>Deselect all</PBtn>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 11.5, color: PT.txD, alignSelf: "center" }}>
                    {sections.filter((s: any) => include[s.section] !== false).length} of {sections.length} included
                  </span>
                </div>
                {sections.map((s: any) => (
                  <PCheck
                    key={s.section}
                    checked={include[s.section] !== false}
                    onChange={(v) => setInclude({ ...include, [s.section]: v })}
                    label={<>{s.section.replace(/_/g, " ")} <span style={{ color: PT.txD }}>({(s.payload || []).length} items)</span></>}
                  />
                ))}
              </>
            )}
          </>
        )}

        {step === 5 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 11 }}>Review</div>
            {[
              ["Organisation", name],
              ["Edition", edition],
              ["Admin", `${adminName || "Tenant Admin"} <${adminEmail}>`],
              ["Sign-in", signin === "sso" ? `SSO (${ssoDomains || "no domains"})` : "Password + forced 2FA"],
              ["Entities", entities || "—"],
              ["Planned users", planned.length ? planned.map((p) => `${p.email}:${p.role}`).join(", ") : "—"],
              ["Integrations", Object.keys(ints).filter((k) => ints[k]).join(", ") || "—"],
              ["IP allowlist", ipAllowlist || "—"],
              ["Defaults copied", `${(sections || []).filter((s: any) => include[s.section] !== false).length} of ${(sections || []).length} surfaces`],
            ].map(([k, v]) => (
              <div key={k as string} style={{ display: "flex", gap: 12, padding: "6px 0", borderBottom: `1px solid ${PT.brdSubtle}`, fontSize: 12.5 }}>
                <div style={{ width: 150, color: PT.txD }}>{k}</div>
                <div style={{ flex: 1 }}>{v as string}</div>
              </div>
            ))}
            <div style={{ marginTop: 15 }}>
              <PBtn
                v="primary"
                disabled={busy || !name.trim() || !adminEmail.trim()}
                onClick={() => submit(`Provisioning new customer "${name.trim()}" (${edition}) with admin ${adminEmail.trim().toLowerCase()}`)}
              >
                {busy ? "Provisioning…" : "Create customer"}
              </PBtn>
            </div>
          </>
        )}
      </PCard>

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <PBtn disabled={step === 0 || busy} onClick={() => setStep((step - 1) as Step)}>Back</PBtn>
        <div style={{ flex: 1 }} />
        {step < 5 && (
          <PBtn v="primary" disabled={!canNext()} onClick={() => setStep((step + 1) as Step)}>Next</PBtn>
        )}
      </div>
    </PSection>
  );
};
